import React, { createContext, useContext, useEffect, useState } from "react";

const STORAGE_KEY = "amazypet_currency";

// Static fallback rates relative to EUR (base currency). The frontend can override
// these by hitting the public exchange-rates endpoint if available; otherwise
// uses these conservative defaults so prices always render.
const FALLBACK_RATES = {
  EUR: 1,
  USD: 1.08,
  GBP: 0.86,
  RON: 4.97,
  BGN: 1.96,
};

const SYMBOLS = {
  EUR: "€",
  USD: "$",
  GBP: "£",
  RON: "lei",
  BGN: "лв",
};

const CurrencyContext = createContext({
  currency: "EUR",
  setCurrency: () => {},
  rates: FALLBACK_RATES,
  symbol: "€",
  convert: (eur) => eur,
  format: (eur) => `€${(eur || 0).toFixed(2)}`,
  available: Object.keys(FALLBACK_RATES),
});

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(
    () => localStorage.getItem(STORAGE_KEY) || "EUR"
  );
  const [rates, setRates] = useState(FALLBACK_RATES);

  useEffect(() => {
    // Best-effort fetch of live rates. If the backend exposes /api/exchange-rates,
    // use those; otherwise stick with fallbacks. Failures are silent.
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/exchange-rates`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data?.rates) return;
        const merged = { EUR: 1, ...FALLBACK_RATES, ...data.rates };
        setRates(merged);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const setCurrency = (c) => {
    if (!rates[c]) return;
    setCurrencyState(c);
    localStorage.setItem(STORAGE_KEY, c);
  };

  const convert = (eurAmount) => {
    const rate = rates[currency] ?? 1;
    return (eurAmount || 0) * rate;
  };

  const format = (eurAmount, opts = {}) => {
    const value = convert(eurAmount);
    const fractionDigits = opts.minimumFractionDigits ?? 2;
    const symbol = SYMBOLS[currency] || currency;
    const rounded = value.toFixed(fractionDigits);
    // Currencies that go after the number
    if (["RON", "BGN"].includes(currency)) {
      return `${rounded} ${symbol}`;
    }
    return `${symbol}${rounded}`;
  };

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        setCurrency,
        rates,
        symbol: SYMBOLS[currency] || currency,
        convert,
        format,
        available: Object.keys(rates),
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);
